import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap } from 'rxjs/operators';
import { SingleSelectServiceFn, TYPE_SEARCH } from './single-select-input.types';

@Component({
  selector: 'single-select-input',
  templateUrl: './single-select-input.component.html',
  styleUrls: ['./single-select-input.component.scss']
})
export class SingleSelectInputComponent implements OnInit, OnChanges {
  @Input() options: any[] = [];
  @Input() label?: string;
  @Input() placeholder?: string;
  @Input() disabled: boolean = false;
  @Input() typeSearch: TYPE_SEARCH = TYPE_SEARCH.LOCAL;
  @Input() valueFn: (option: any) => any = (option) => option;
  @Input() displayFn: (option: any) => string = (option) => option?.toString() || '';
  @Input() serviceFn?: SingleSelectServiceFn;
  @Input() initialValue?: any;
  /** Valor aplicado al pulsar limpiar; si no se define, se emite `null`. */
  @Input() clearValue?: unknown;

  @Output() outputSelect = new EventEmitter<any>();

  control = new FormControl('');
  filteredOptions: Observable<any[]>;
  isOpenPanel: boolean = true;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled']) {
      this._syncDisabledState();
    }
    if (changes['initialValue'] && !changes['initialValue'].firstChange) {
      this._applyInitialValue(this.initialValue);
    }
  }

  ngOnInit(): void {
    this._applyInitialValue(this.initialValue);
    this._syncDisabledState();
    this.setupFiltering();
  }

  hasControlValue(): boolean {
    const v = this.control.value;
    return v !== null && v !== undefined && v !== '';
  }

  private _applyInitialValue(value: unknown): void {
    if (value !== undefined && value !== null && value !== '') {
      this.control.setValue(value, { emitEvent: false });
      return;
    }
    this.control.setValue('', { emitEvent: false });
  }

  private _syncDisabledState(): void {
    if (this.disabled) {
      if (this.control.enabled) {
        this.control.disable({ emitEvent: false });
      }
    } else if (this.control.disabled) {
      this.control.enable({ emitEvent: false });
    }
  }

  private setupFiltering(): void {
    if (this.typeSearch === TYPE_SEARCH.LOCAL) {
      this.filteredOptions = this.control.valueChanges.pipe(
        startWith(''),
        map(value => this._filterLocal(value))
      );
    } else if (this.typeSearch === TYPE_SEARCH.SERVICE && this.serviceFn) {
      this.filteredOptions = this.control.valueChanges.pipe(
        startWith(''),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(term => this.serviceFn!(term))
      );
    } else {
      this.filteredOptions = of(this.options);
    }
  }

  private _filterLocal(value: unknown): unknown[] {
    if (value === null || value === undefined || value === '') {
      return this.options;
    }

    const filterValue = typeof value === 'string' ? value.toLowerCase() : this.displayFn(value).toLowerCase();
    
    return this.options.filter(option => {
      const optionValue = this.displayFn(option).toLowerCase();
      return optionValue.includes(filterValue);
    });
  }

  onSelect(value: any): void {
    this.outputSelect.emit(this.valueFn(value));
    // Reiniciar el filtrado para mostrar todas las opciones
    this.resetFilter();
  }

  onClear(): void {
    const next = this.clearValue !== undefined ? this.clearValue : null;
    if (next !== null && next !== undefined && next !== '') {
      this.control.setValue(next, { emitEvent: false });
    } else {
      this.control.setValue('', { emitEvent: false });
    }
    this.outputSelect.emit(next);
  }

  isSelected(option: unknown): boolean {
    const currentValue = this.control.value;
    if (currentValue === undefined || currentValue === null || currentValue === '') {
      return false;
    }
    
    // Comparar por referencia si es el mismo objeto
    if (currentValue === option) return true;
    
    // Comparar por propiedades si son objetos
    if (typeof currentValue === 'object' && typeof option === 'object') {
      return this.valueFn(currentValue) === this.valueFn(option);
    }
    
    // Comparar por valor si son primitivos
    return this.valueFn(currentValue) === this.valueFn(option);
  }

  private resetFilter(): void {
    setTimeout(() => {
      this.setupFiltering();
    }, 0);
  }
}
